import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { WeezHeader, Card, Button, Select, Section, Message, RadioLabel, Input, Form, ToastContext, TooltipHelp, DateTimeInput } from '@weezevent/nacre';
import { useForm } from '../../../utils/validators';
import { HOOK_CONFIG } from '../webserviceConfig';
import { DISTRIBUTORS, WEBSERVICE_FORMS, getInitialValues, getValidators, getTranslatedCreateFormByOptions } from '../webserviceUtils';
import i18n from 'i18next';
import moment from 'moment-timezone';
import useAppContext from '../../../context/useAppContext';
import SubscriptionModel from '../../../models/subscription';
import { useHistory } from 'react-router-dom';
import Styles from './createWebservice.module.css';
import { usePermissions } from '../../../user/permissions';

function TypeComponent({ onCardDone, formState, handleTypeChange, disableHook }) {
    const { t } = useTranslation();
    return (
        <div>
            <div>{t('ticket.webservice.create.type-label')}</div>
            <div>
                <RadioLabel
                    inline
                    bordered
                    name="ticket-provider"
                    checked={formState.type !== 'hook'}
                    label={t('ticket.webservice.create.option-ticket-provider')}
                    onChange={() => handleTypeChange(null)}
                />
                <RadioLabel
                    inline
                    bordered
                    name="custom-import"
                    checked={formState.type === 'hook'}
                    label={t('ticket.webservice.create.option-custom-import')}
                    onChange={() => handleTypeChange('hook')}
                    disabled={disableHook}
                />
            </div>
            <Button primary onClick={onCardDone}>
                {t('ticket.webservice.create.continue')}
            </Button>
        </div>
    );
}

TypeComponent.propTypes = {
    onCardDone: PropTypes.func.isRequired,
    formState: PropTypes.object,
    handleTypeChange: PropTypes.func.isRequired,
    disableHook: PropTypes.bool
};

function SelectDistributors({ formState, setFormState, formErrors, setFormStateObject, onSubmit, isSubmitting, setFormErrors, createFormByOptions, handleTypeChange }) {
    const { t } = useTranslation();

    // render corresponding form depending on selected distributor type
    const renderFields = React.useCallback(() => {
        const Component = WEBSERVICE_FORMS[formState.type];
        if (!Component) return null;
        return <Component formState={formState} setFormState={setFormState} formErrors={formErrors} />;
    }, [formState, formErrors, setFormState]);

    const isDisabled = Boolean(isSubmitting || !formState.type || Object.keys(formErrors).length > 0);

    const options = [{ text: t('ticket.webservice.form.choiceDistributor'), value: '' }, ...DISTRIBUTORS];

    return (
        <div>
            <div>
                <Form.Label>{t('ticket.webservice.create.information-label')}</Form.Label>
                <Select className={Styles['distributors-select']} options={options} value={formState.type || ''} onChange={(_, { value }) => handleTypeChange(value)} />
                {formState.type && <Section>{renderFields()}</Section>}
                <Form.Label>{t('ticket.webservice.form.createFormBy.label')}</Form.Label>
                <Select
                    options={createFormByOptions}
                    placeholder={t('ticket.webservice.form.choiceProvider')}
                    onChange={(_, { value }) => setFormState('create_form_by', value)}
                    className={Styles['provider-select']}
                    value={formState.create_form_by}
                />
            </div>
            <Button primary disabled={isDisabled} onClick={onSubmit}>
                {t('ticket.common.cta.save')}
            </Button>
        </div>
    );
}

SelectDistributors.propTypes = {
    formState: PropTypes.object,
    setFormState: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    setFormStateObject: PropTypes.func.isRequired,
    formErrors: PropTypes.object,
    isSubmitting: PropTypes.bool,
    setFormErrors: PropTypes.func,
    createFormByOptions: PropTypes.array,
    handleTypeChange: PropTypes.func.isRequired
};

function CreateImport({ formState, setFormState, timezone, onSubmit, isSubmitting, formErrors, createFormByOptions }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const currentYear = new Date().getFullYear();

    // form error if selected date is in the past
    const warningPastDate = React.useMemo(() => {
        const now = moment()
            .tz(timezone)
            .format();
        if (formState.end_date && formState.end_date < now) {
            return t('ticket.webservice.create.import.date-error');
        }
        return false;
    }, [timezone, formState.end_date, t]);

    const onChangeProvider = React.useCallback((_, { value }) => setFormState('default_provider', value), [setFormState]);

    return (
        <div>
            <div className={Styles['hook-name']}>{t('ticket.webservice.create.import.name')}</div>
            <Form.Field>
                <Input value={formState.name || ''} onChange={({ target: { value } }) => setFormState('name', value)} />
            </Form.Field>
            {formErrors.name && <Form.FieldErrors errors={[t(formErrors.name)]} />}

            <div className={Styles['modal-settings-event-section']}>
                <div className={Styles['date-container']}>
                    <Form.Field>
                        <DateTimeInput
                            className={Styles['date-time-input']}
                            timeLabel={
                                <>
                                    {t('ticket.common.time.end')}
                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                </>
                            }
                            dateLabel={
                                <>
                                    {t('ticket.common.date.end')}
                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                </>
                            }
                            onChange={date => setFormState('end_date', date)}
                            value={formState.end_date}
                            dateProps={{ minYear: currentYear, minDate: moment() }}
                            locale={locale}
                            timezone={timezone}
                        />
                    </Form.Field>
                    <div className={Styles['timezone-container']}>
                        <div>{timezone}</div>
                    </div>
                </div>
                {Boolean(warningPastDate) && <Form.FieldErrors errors={[warningPastDate]} />}
            </div>
            <div>
                <Form.Label>
                    {t('ticket.webservice.form.choiceProviderTitle')}
                    <TooltipHelp title={t('ticket.webservice.form.choiceProviderTooltip')} className={Styles['provider-option-select']} />
                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                </Form.Label>
                <Select
                    options={HOOK_CONFIG.hookProviderOptions}
                    placeholder={t('ticket.webservice.form.choiceProvider')}
                    onChange={onChangeProvider}
                    className={Styles['provider-select']}
                    value={formState.default_provider || ''}
                />
                <Form.Label>{t('ticket.webservice.form.createFormBy.label')}</Form.Label>
                <Select
                    options={createFormByOptions}
                    placeholder={t('ticket.webservice.form.choiceProvider')}
                    onChange={(_, { value }) => setFormState('create_form_by', value)}
                    className={Styles['provider-select']}
                    value={formState.create_form_by}
                />
            </div>
            <Button primary disabled={isSubmitting || !formState.name} onClick={onSubmit}>
                {t('ticket.webservice.create.import.cta')}
            </Button>
        </div>
    );
}

CreateImport.propTypes = {
    formState: PropTypes.object,
    setFormState: PropTypes.func.isRequired,
    timezone: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    formErrors: PropTypes.object,
    createFormByOptions: PropTypes.array
};

export default function CreateWebservice({ organizationId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedType, setSelectedType] = React.useState(null);

    const initialFormState = React.useMemo(() => getInitialValues(selectedType), [selectedType]);
    const validators = React.useMemo(() => getValidators(selectedType), [selectedType]);

    const [formState, setFormState, setFormStateObject, formErrors, formValidate, setFormErrors] = useForm(initialFormState, validators);

    const handleTypeChange = React.useCallback(
        value => {
            setSelectedType(value);
            setFormStateObject(getInitialValues(value), true);
            setFormErrors({});
        },
        [setFormStateObject, setFormErrors]
    );

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) return;

        setIsSubmitting(true);
        const newSubscriptionModel = new SubscriptionModel();
        newSubscriptionModel.type = formState.type;

        if (formState.type === 'hook') {
            HOOK_CONFIG.fields.forEach(field => {
                if (formState[field] !== undefined) {
                    newSubscriptionModel[field] = formState[field];
                }
            });
        } else {
            const formStateCopy = { ...formState };
            delete formStateCopy.default_provider;
            newSubscriptionModel.setInfo(formStateCopy);
        }

        newSubscriptionModel
            .save({ organizationId })
            .then(({ response }) => {
                toast.success(t('ticket.webservice.creationSuccess'));
                const path = response.type === 'hook' ? `/ticket/O${organizationId}/webservices/${response.id}/attendees` : `/ticket/O${organizationId}/webservices`;
                history.push(path);
            })
            .catch(error => {
                let message = t([`ticket.webservice.statuses.${error.code}`, 'ticket.webservice.creationError']);
                if (error.response?.includes('This subscription already exists and is running')) {
                    message = t('ticket.webservice.creationErrorSameLogin');
                }
                toast.error(message);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [organizationId, formValidate, formState, toast]);

    return (
        <>
            <WeezHeader title={t('ticket.webservice.create.title')} backLink={{ onClick: () => history.push(`/ticket/O${organizationId}/webservices`) }} />
            <Card.FormManager>
                <Card.FormStep
                    title={t('ticket.webservice.create.type-title')}
                    subtitle={t('ticket.webservice.create.type-subtitle')}
                    editLabel={t('ticket.common.action.edit')}
                    summary={
                        <>
                            <span>{t('ticket.webservice.create.summary')}</span>
                            <span className={Styles['summary']}>
                                {formState.type === 'hook' ? t('ticket.webservice.create.option-custom-import') : t('ticket.webservice.create.option-ticket-provider')}
                            </span>
                        </>
                    }
                    rightComponent={
                        <Message type="tips" title={t('notifications.tips_title')} mini>
                            <Trans i18nKey="ticket.webservice.create.tips-message"></Trans>
                        </Message>
                    }
                >
                    <TypeComponent
                        formState={formState}
                        handleTypeChange={handleTypeChange}
                        disableHook={!permissions.hasCapacity('WendyCapacity.WEBSERVICES_PUSH', organizationId)}
                    />
                </Card.FormStep>

                <Card.FormStep
                    title={t('ticket.webservice.create.information-title')}
                    subtitle={
                        formState.type === 'hook'
                            ? t('ticket.webservice.create.information-subtitle-custom-import')
                            : t('ticket.webservice.create.information-subtitle-ticket-provider')
                    }
                    rightComponent={
                        formState.type === 'hook' ? (
                            <Message type="tips" title={t('notifications.tips_title')} mini>
                                <Trans i18nKey="ticket.webservice.create.import.tips-message"></Trans>
                            </Message>
                        ) : (
                            <div></div>
                        )
                    }
                >
                    {formState.type !== 'hook' ? (
                        <SelectDistributors
                            formState={formState}
                            setFormState={setFormState}
                            setFormStateObject={setFormStateObject}
                            formErrors={formErrors}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            setFormErrors={setFormErrors}
                            createFormByOptions={getTranslatedCreateFormByOptions(t)}
                            handleTypeChange={handleTypeChange}
                        />
                    ) : (
                        <CreateImport
                            formState={formState}
                            setFormState={setFormState}
                            timezone={organization.timezone}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            formErrors={formErrors}
                            createFormByOptions={getTranslatedCreateFormByOptions(t)}
                        />
                    )}
                </Card.FormStep>
            </Card.FormManager>
        </>
    );
}

CreateWebservice.propTypes = {
    organizationId: PropTypes.string.isRequired
};
